import React from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteProps,
} from 'react-router-dom';
import { Theme, ThemeProvider } from 'theme-ui';
import ErrorBoundary from '../../ErrorBoundary';
import { ProvideAuth, useAuth } from '../hooks/use-auth';
import { ProvideUser, useUser } from '../hooks/use-user';
import theme from '../ui/theme';
import AccountSettings from './account-settings/AccountSettings';
import ForgotPassword from './forgot-password/ForgotPassword';
import Login from './login/Login';
import Referrals from './referrals/Referrals';
import ResumeEditor from './resume-editor/ResumeEditor';
import RouteNotFound from './route-not-found/RouteNotFound';
import TrackPageViews from './TrackPageViews';
import UpgradeThankYou from './upgrade-thank-you/UpgradeThankYou';
import Upgrade from './upgrade/Upgrade';
import { ResumeProvider } from 'v1/context/resume-context';

function App() {
  return (
    <ErrorBoundary fullscreen={true} location="v1-root">
      <Router>
        <ThemeProvider theme={theme as Theme}>
          <ProvideAuth>
            <ProvideUser>
              <Switch>
                <Route exact path="/">
                  <Redirect to="/setup" />
                </Route>
                <Route exact path="/create-account">
                  <Redirect to="/setup" />
                </Route>
                <Route path="/login">
                  <Login />
                </Route>
                <Route path="/forgot">
                  <ForgotPassword />
                </Route>
                <Route path="/setup">
                  <RedirectToNewSetup />
                </Route>
                <PrivateRoute path="/account">
                  <AccountSettings />
                </PrivateRoute>
                <PrivateRoute path="/upgrade">
                  <Upgrade />
                </PrivateRoute>
                <PrivateRoute path="/friends">
                  <Referrals />
                </PrivateRoute>
                <PrivateRoute exact path="/upgrade-thank-you">
                  <UpgradeThankYou />
                </PrivateRoute>
                <PrivateRoute exact path="/resumes">
                  <RedirectToResume />
                </PrivateRoute>
                <PrivateRoute exact path="/resumes/share">
                  <RedirectToResume path="/share" />
                </PrivateRoute>
                <PrivateRoute exact path="/resumes/preview">
                  <RedirectToResume path="/preview" />
                </PrivateRoute>
                <PrivateRoute path="/resumes/:resumeId">
                  <ResumeProvider>
                    <ResumeEditor />
                  </ResumeProvider>
                </PrivateRoute>
                <Route path="*">
                  <RouteNotFound />
                </Route>
              </Switch>
              <TrackPageViews />
            </ProvideUser>
          </ProvideAuth>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

function RedirectToNewSetup() {
  // Redirect the user to the new /v2/setup flow
  window.location.pathname = '/v2/setup';
  return null;
}

// Send unauthenticated users to login page
function PrivateRoute({ children, ...rest }: RouteProps) {
  const auth = useAuth();

  // Don't do anything until the auth has loaded
  if (auth.user === null) return null;

  return (
    <Route
      {...rest}
      render={({ location }) =>
        auth.user ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

function RedirectToResume({ path = '/write' }: { path?: string }) {
  const user = useUser();
  if (user) {
    // Pick a random resume to start with
    const aResumeId = Object.keys(user.resumes || {})[0];
    if (aResumeId) {
      return (
        <Redirect
          to={`/resumes/${aResumeId}${path}${window.location.search}`}
        />
      );
    } else {
      // The user has no resumes. Send them to setup to create one.
      return <Redirect to="/setup" />;
    }
  } else if (user === null) {
    // Waiting for user to be fetched
    return null;
  } else {
    // No user. Send to setup.
    return <Redirect to="/setup" />;
  }
}

export default App;
